/**
 * Role capabilities. The server and the client both read this table, so an
 * API guard and the control that calls it use the same rule.
 *
 * Staff roles make a chain. Each role gets all capabilities of the role below
 * it, and adds its own:
 *
 *   internal → designer → admin → superadmin
 *
 * - designer: + default scene editing.
 * - admin: + upload moderation, analytics, all channels, the backgrounds
 *   that each stage and set offers and their order in a set, feature flags (not the superadmin
 *   targets) and templates.
 * - superadmin: + operations that change the app for every user. Only from
 *   the SUPERADMIN_EMAILS / CI_ADMIN_EMAILS env vars.
 *
 * partner and external are not in the chain.
 */

export type UserRole = "superadmin" | "admin" | "internal" | "designer" | "partner" | "external";

export const ALL_ROLES: readonly UserRole[] = [
  "superadmin",
  "admin",
  "internal",
  "designer",
  "partner",
  "external",
];

/** Roles that an admin can put in the user_roles collection. Superadmin is env-only. */
export const ASSIGNABLE_ROLES: readonly UserRole[] = ALL_ROLES.filter((r) => r !== "superadmin");

/** Roles that a superadmin can view the app as, to see how each role works. */
export const VIEW_AS_ROLES: readonly UserRole[] = ALL_ROLES.filter((r) => r !== "superadmin");

/**
 * The role that the app uses for a caller. A superadmin can view the app as a
 * role from VIEW_AS_ROLES. A request from every other role has no effect, so
 * it cannot give more access.
 */
export function effectiveRole<R extends string>(realRole: R, viewAs: string | null | undefined): R | UserRole {
  if (realRole !== "superadmin") return realRole;
  const role = VIEW_AS_ROLES.find((r) => r === viewAs);
  return role ?? realRole;
}

/** The role that each role gets all capabilities from. */
const INHERITS: Record<UserRole, UserRole | null> = {
  superadmin: "admin",
  admin: "designer",
  designer: "internal",
  internal: null,
  partner: null,
  external: null,
};

export type Capability =
  | "manageUsers"
  | "manageFlags"
  | "editStageOrder"
  | "editSelectorVisibility"
  | "editBgList"
  | "manageTemplates"
  | "editBackgroundVisibility"
  | "editSetBgOrder"
  | "viewAnalytics"
  | "moderateUploads"
  | "viewAllChannels"
  | "skipStagingNotice"
  | "editDefaultScenes";

/** Capabilities that each role adds on top of the role it inherits. */
const GRANTS: Record<UserRole, readonly Capability[]> = {
  superadmin: ["manageUsers", "editStageOrder", "editSelectorVisibility", "editBgList"],
  admin: [
    // Admin console: Feature Flags and Templates (Sergey, 2026-11-03). The
    // superadmin target of a flag stays superadmin-only (api/feature-flags.ts).
    "manageFlags",
    "manageTemplates",
    // Hide or offer a background tile per stage and per set. Products, sets
    // and layouts stay editSelectorVisibility (superadmin).
    "editBackgroundVisibility",
    // The own background order of a releases set. The product's list (order
    // of every set, removals, publish) stays editBgList (superadmin).
    "editSetBgOrder",
    "viewAnalytics",
    "moderateUploads",
    "viewAllChannels",
    "skipStagingNotice",
  ],
  designer: ["editDefaultScenes"],
  internal: [],
  partner: [],
  external: [],
};

function isRole(role: unknown): role is UserRole {
  return typeof role === "string" && Object.hasOwn(INHERITS, role);
}

/** The role and every role it inherits, from the role down. Empty for an unknown role. */
export function roleChain(role: string | null | undefined): UserRole[] {
  const chain: UserRole[] = [];
  for (let r: UserRole | null = isRole(role) ? role : null; r; r = INHERITS[r]) chain.push(r);
  return chain;
}

export function can(role: string | null | undefined, capability: Capability): boolean {
  return roleChain(role).some((r) => GRANTS[r].includes(capability));
}

/**
 * Can a flag manager change the flag target of `role`? An admin manages the
 * flags too, but only a superadmin changes the superadmin target: an admin
 * cannot take a control away from a superadmin.
 */
export function mayChangeFlagTarget(callerRole: string | null | undefined, role: string): boolean {
  return can(callerRole, "manageFlags") && (role !== "superadmin" || callerRole === "superadmin");
}

/** Admin console sections in nav order, each with the capability that opens it. */
export const ADMIN_SECTIONS = [
  {path: "flags", label: "Feature Flags", capability: "manageFlags"},
  {path: "users", label: "User Roles", capability: "manageUsers"},
  {path: "analytics", label: "Analytics", capability: "viewAnalytics"},
  {path: "templates", label: "Templates", capability: "manageTemplates"},
] as const satisfies readonly {path: string; label: string; capability: Capability}[];

export function adminSectionsFor(role: string | null | undefined) {
  return ADMIN_SECTIONS.filter((s) => can(role, s.capability));
}
